import { UserProfile } from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import { useEffect, useRef } from "react";
import { apiReq } from "./apiReq";
import { useStoreData } from "./store/store";
import { useLastGood } from "./useLastGood";
import { isIncompleteProfileError } from "./useProfileDeck";

/**
 * "available" while anyone at the event is left for the viewer to Spark,
 * "done" once they have seen everyone (or have no pre-event deck: Friends
 * Only, no seat, or the event has ended), "loading" until the deck answers.
 * Never a count.
 */
export type PreSparkState = "loading" | "available" | "done";

/**
 * Whether the viewer has anyone left to Spark at an event. `sparkId` is an
 * event they hold a seat at; leave it undefined when they hold none, and the
 * answer is "done". The deck is asked while `active` is true, and again each
 * time it turns back on, so a page that stays mounted under the deck turns
 * over when the viewer comes back to it. While it is off, the last answer
 * for this event stands.
 */
export const usePreSparkState = (
  sparkId: number | undefined,
  active = true
): PreSparkState => {
  const friendsOnly = useStoreData().user?.friends_only ?? false;
  const [deckReq, deckReqState] = useReq<UserProfile[]>(apiReq);
  // Which event the last request asked about, so an answer for one event is
  // never reported for another.
  const requestedFor = useRef(sparkId);
  useEffect(() => {
    if (!sparkId || !active || friendsOnly) return;
    requestedFor.current = sparkId;
    deckReq.get(`/spark/${sparkId}/next-profiles`);
  }, [deckReq, sparkId, active, friendsOnly]);

  // Hold the last answer through a refetch so the page doesn't flicker back
  // to its loading shape every time the viewer returns.
  const lastDeck = useLastGood(
    deckReqState.result
      ? { sparkId: requestedFor.current, deck: deckReqState.result }
      : null
  );

  if (!sparkId || friendsOnly) return "done";
  if (lastDeck && lastDeck.sparkId === sparkId) {
    return lastDeck.deck.length ? "available" : "done";
  }
  if (deckReqState.error) {
    // An incomplete profile is asked for on the deck itself, so still send
    // them there; anything else, leave Sparking out.
    return isIncompleteProfileError(deckReqState.error.code)
      ? "available"
      : "done";
  }
  return "loading";
};
